/**
 * The evidence-weighted view: what the canvas draws when it is asked to size a
 * claim by what has been established about it rather than by what the model
 * rated it.
 *
 * The renderer maps two numbers to form — `confidence` to strand thickness and
 * `novelty` to the halo and, with confidence, to the node's radius — and both
 * numbers are the model's own opinion of its own work. That is the right default
 * for a graph being grown: it is the only signal a fresh claim has. It is the
 * wrong picture of a graph being READ, because a source check, another house's
 * verdict, three lenses and a recorded outcome all leave the two numbers exactly
 * where they were, so the canvas draws a refuted mechanism as thick as a checked
 * one and a claim nobody looked at as bright as one three instruments held.
 *
 * This module substitutes the two numbers, and nothing else, for the canvas
 * alone. `evidenceWeights` reads records the model did not write about itself
 * — the same four the ladder in `core/rung.ts` reads — and returns a strength
 * and a glow on the same [0, 1] scales the renderer already speaks, so no
 * renderer code changes and every other reader of a node (the card, the cues,
 * the policy) keeps the real numbers. The glow is `measuredNovelty` when a
 * search measured one and nothing otherwise: in this view a claim glows for
 * novelty only when prior art was actually looked for.
 *
 * The weights are a SUM of named terms, exported so the key bar can print them.
 * Not a score of the claim — a claim with every instrument in its favour is
 * drawn heavy, not called true — and not a ranking: nothing sorts by it.
 */

import { auditLive, outcomeLive, verificationLive, type ReasonNode, type Session } from './types'
import { panelChip, tallyPanel } from './panel'

export type Weighting = 'model' | 'evidence'

/** How much each grounding record adds to a claim's strength. Sums to one
 *  exactly when every instrument has spoken in the claim's favour. */
export const EVIDENCE_TERMS = {
  /** Every claim starts here, so an unexamined strand is thin rather than absent. */
  base: 0.12,
  /** A live source check that landed `supported`. */
  supported: 0.28,
  /** A live source check that landed `mixed` — some support, some dispute. */
  mixed: 0.1,
  /** A live pass from a model of another house. */
  auditPass: 0.2,
  /** A panel that agreed the claim HOLDS — `DECIDED_PAIR_FLOOR` seats decided,
   *  none dissented, and what they agreed on was in the claim's favour. Not
   *  merely "did not split": a panel where two seats went undecidable has no
   *  disagreement in it and no agreement either, and one that unanimously found
   *  the claim fails agreed about the opposite thing. */
  panelAgreed: 0.1,
  /** A recorded outcome that supported the claim. */
  observed: 0.3,
} as const

/** A refuted claim's strength: nothing but the base. The channel colour already
 *  says refuted; a thick magenta strand would say it loudly and wrongly. */
export interface EvidenceWeight {
  strength: number
  glow: number
  /** The terms that summed to `strength`, by name — the WHY line for the key. */
  terms: Array<keyof typeof EVIDENCE_TERMS>
}

export function evidenceWeights(n: ReasonNode): EvidenceWeight {
  const terms: Array<keyof typeof EVIDENCE_TERMS> = ['base']
  let strength: number = EVIDENCE_TERMS.base
  // `outcomeLive` beside `verificationLive`, and the asymmetry between them was
  // the bug: every other term here is read through its record's own liveness
  // rule and the outcome was not, so a result recorded against wording the user
  // has replaced drew the strand for a claim it never touched. Refuted is the
  // louder direction — it takes a claim to base strength, which is the canvas
  // saying "nothing supports this" about a proposition nobody tested.
  const refuted =
    (n.outcome?.bearing === 'refuted' && outcomeLive(n)) ||
    (n.verification?.reading === 'refuted' && verificationLive(n))
  if (!refuted) {
    const v = n.verification
    if (v && verificationLive(n)) {
      if (v.reading === 'supported') {
        strength += EVIDENCE_TERMS.supported
        terms.push('supported')
      } else if (v.reading === 'mixed') {
        strength += EVIDENCE_TERMS.mixed
        terms.push('mixed')
      }
    }
    if (n.audit && auditLive(n) && n.audit.verdict === 'pass') {
      strength += EVIDENCE_TERMS.auditPass
      terms.push('auditPass')
    }
    // `panelChip` and not `!panelSplit`, because those are different questions and
    // only one of them is agreement. `panelSplit` is exact-equality over the
    // DECIDED votes, so it is false on a panel where one seat decided and two came
    // back undecidable — a panel with nobody to agree with. The chip is where the
    // app already answers this, floored at `DECIDED_PAIR_FLOOR`, so asking it is
    // also what keeps the canvas and the panel sheet saying the same thing about
    // one panel. It allocates and may: this runs in `useGraphFrame`'s memo, never
    // below `Renderer.draw()`.
    //
    // AND THE DIRECTION IT AGREED IN. The chip answers "did the lenses converge",
    // which is true of a panel that unanimously found the claim FAILS — the chip
    // is right to say so, since converging is what it reports, and this file is
    // wrong to read convergence as favour. Every term here is evidence FOR the
    // claim, and a panel that agreed against it is the opposite: it lands a
    // contradiction elsewhere, and thickening the strand for it would draw the
    // best-attacked claim on the graph as one of the best-supported.
    const panelVerdict =
      n.panel && panelChip(n.panel) === 'agreed' ? tallyPanel(n.panel).majority : null
    if (panelVerdict === 'holds' || panelVerdict === 'holds-weaker') {
      strength += EVIDENCE_TERMS.panelAgreed
      terms.push('panelAgreed')
    }
    if (n.outcome?.bearing === 'supported' && outcomeLive(n)) {
      strength += EVIDENCE_TERMS.observed
      terms.push('observed')
    }
  }
  const measured =
    n.verification && verificationLive(n) ? n.verification.measuredNovelty : undefined
  return { strength: Math.min(1, strength), glow: measured ?? 0, terms }
}

/**
 * The nodes the canvas draws under a weighting.
 *
 * Under `model` the session's own map, by identity, so the default view costs
 * nothing. Under `evidence` a fresh map whose every node carries the weights in
 * the two fields the renderer reads; every other field is the node's own. The
 * seed keeps its numbers: it is the question and has no grounding to weigh.
 */
export function weightedNodes(nodes: Session['nodes'], weighting: Weighting): Session['nodes'] {
  if (weighting === 'model') return nodes
  const out: Session['nodes'] = {}
  for (const id in nodes) {
    const n = nodes[id]!
    if (n.parentId === null) {
      out[id] = n
      continue
    }
    const w = evidenceWeights(n)
    out[id] = { ...n, confidence: w.strength, novelty: w.glow }
  }
  return out
}
